<template>
  <section :id="formId" class="page-form" :class="classes">
    <div class="page-form__inner" :class="{ 't-container': useContainer }">
      <transition name="fadeRight">
        <template v-if="showResultForm">
          <FormErrorResult v-if="isError" :title="errorTitle" :text="errorText">
            <template #error-actions>
              <slot name="error-actions"></slot>
            </template>
          </FormErrorResult>
          <FormSuccessResult v-else :title="successTitle" :text="successText">
            <template #success-actions>
              <slot name="success-actions"></slot>
            </template>
          </FormSuccessResult>
        </template>
      </transition>

      <transition name="fadeRight">
        <div v-show="!showResultForm" class="page-form__content">
          <div class="page-form__header">
            <h2 v-if="formTitle" class="page-form__title">
              {{ normalizeHtmlText(formTitle) }}
            </h2>
            <div v-if="!isStepMode && formDescription" class="page-form__description">
              {{ normalizeHtmlText(formDescription) }}
            </div>
            <div v-if="$slots['header-actions']" class="page-form__header-actions">
              <slot name="header-actions" />
            </div>
          </div>

          <FormStepper v-if="showFormStepper" :current-step="currentStep" :step-idx="stepIdx" :steps-count="steps.length" />

          <CoreForm
            class="page-form__form"
            :formConfig="formConfig"
            :action="action"
            :extra-field-names="extraFieldNames"
            :extra-submit-data="extraSubmitData"
            :before-submit-fn="beforeSubmitFn"
            :agreements="agreements"
            :back-label="backLabel"
            :next-label="nextLabel"
            :submit-label="submitLabel"
            @form:init="onFormInit"
            @form:validation-error="onValidationError"
            @form:back-step="onBackStep"
            @form:next-step="onNextStep"
            @form:submit-success="onSuccessSubmit"
            @form:submit-error="onErrorSubmit"
          >
            <template v-for="(_, slotName) in $scopedSlots" v-slot:[slotName]="slotProps">
              <slot :name="slotName" v-bind="slotProps" />
            </template>
          </CoreForm>
        </div>
      </transition>
    </div>
  </section>
</template>

<script>
import { FORM_STATUS } from '@shared/const/form/form.status'
import FormStepper from '@shared/ui/public/forms/components/FormStepper.vue'
import FormActions from '@shared/ui/public/forms/components/FormActions.vue'
import FormErrorResult from '@shared/ui/public/forms/components/FormErrorResult.vue'
import FormSuccessResult from '@shared/ui/public/forms/components/FormSuccessResult.vue'
import CoreForm from '@shared/ui/public/forms/CoreForm.vue'
import { DEFAULT_AGREEMENTS, DEFAULT_EXTRA_FIELD_NAMES } from '@shared/const/form/form.field'
import { DELAY_BEFORE_FORM_RESET, FORM_LAYOUT } from '@shared/const/form/form.data'
import { normalizeHtmlText } from '@shared/utils/string'

export default {
  name: 'PageForm',
  components: {
    FormStepper,
    FormActions,
    FormErrorResult,
    FormSuccessResult,
    CoreForm,
  },
  props: {
    formConfig: {
      type: Object,
      required: true,
    },
    action: {
      type: String,
      required: true,
    },
    layout: {
      type: String,
      default: () => FORM_LAYOUT.DEFAULT,
    },
    useContainer: {
      type: Boolean,
      default: true,
    },
    extraFieldNames: {
      type: Array,
      default: () => DEFAULT_EXTRA_FIELD_NAMES,
    },
    extraSubmitData: {
      type: Object,
      default: () => ({}),
    },
    beforeSubmitFn: {
      type: Function,
      default: $ => $,
    },
    agreements: {
      type: Array,
      default: () => DEFAULT_AGREEMENTS,
    },
    backLabel: {
      type: String,
      default: window.staticText.forms.back,
    },
    nextLabel: {
      type: String,
      default: window.staticText.forms.then,
    },
    submitLabel: {
      type: String,
      default: window.staticText.forms.regBtn,
    },
    successTitle: {
      type: String,
      default: window.staticText.modal.successTitle,
    },
    successText: {
      type: String,
      default: window.staticText.modal.successText2,
    },
    errorTitle: {
      type: String,
      default: window.staticText.modal.errorTitle,
    },
    errorText: {
      type: String,
      default: window.staticText.modal.errorText,
    },
  },
  data() {
    return {
      formState: null,
      publicApi: null,
    }
  },
  computed: {
    form() {
      return this.formState?.form || {}
    },
    formId() {
      return this.publicApi?.formId()
    },
    fields() {
      return this.formState?.fields || []
    },
    isStepMode() {
      return this.publicApi?.isStepMode()
    },
    showFormStepper() {
      return this.currentStep?.title || this.currentStep?.description
    },
    formStatus() {
      return this.formState?.status || ''
    },
    currentStep() {
      return this.steps[this.stepIdx]
    },
    stepIdx() {
      return this.formState?.currentStep || 0
    },
    steps() {
      return this.form.steps || []
    },
    formTitle() {
      return this.form.title
    },
    formDescription() {
      return this.form.description
    },
    classes() {
      if (!this.formStatus) return []

      return [
        `page-form--status-${this.formStatus}`,
        `page-form--mode-${this.form.mode}`,
        `page-form--theme-${this.form.theme}`,
        `page-form--layout-${this.layout}`,
        {
          'page-form--submitting': this.formStatus === FORM_STATUS.SUBMITTING,
          'page-form--success': this.formStatus === FORM_STATUS.SUCCESS,
          'page-form--error': this.formStatus === FORM_STATUS.ERROR,
        },
      ]
    },
    showResultForm() {
      return [FORM_STATUS.SUCCESS, FORM_STATUS.ERROR].includes(this.formStatus)
    },
    isError() {
      return this.formStatus === FORM_STATUS.ERROR
    },
  },
  methods: {
    normalizeHtmlText,
    onFormInit({ formState, publicApi }) {
      this.formState = formState
      this.publicApi = publicApi
    },
    onSuccessSubmit({ formId }) {
      if (this.formState?.form?.redirect) {
        window.location.assign(this.formState.form.redirect)
        return
      }

      this.$root.scrollToBlock(`#${formId}`)

      setTimeout(async () => {
        await this.publicApi.reset()
        this.publicApi.setStatus(FORM_STATUS.INIT)
      }, DELAY_BEFORE_FORM_RESET)
    },
    onErrorSubmit({ formId }) {
      this.$nextTick(() => {
        this.$root.scrollToBlock(`#${formId}`)

        setTimeout(() => {
          this.publicApi.setStatus(FORM_STATUS.EDITING)
        }, DELAY_BEFORE_FORM_RESET)
      })
    },
    onValidationError({ formId }) {
      this.$nextTick(() => {
        this.$root.scrollToBlock(`#${formId} .form-fields__item--error`)
      })
    },
    onBackStep({ formId }) {
      this.$root.scrollToBlock(`#${formId}`)
    },
    onNextStep({ formId }) {
      this.$root.scrollToBlock(`#${formId}`)
    },
  },
}
</script>

<style lang="sass">
@import "~@src/legacy/sass/params"

.page-form
  &--theme-cyan
    --page-form-bg-color: #{$cyan}
    --page-form-text-color: #{$white}
    --form-stepper-track-color: #33C0CA
    --form-stepper-value-color: #99DFE5

  &--theme-white
    --page-form-bg-color: #{$white}
    --page-form-text-color: #{$black}
    --form-stepper-track-color: #99DFE5
    --form-stepper-value-color: #33C0CA

  background-color: var(--page-form-bg-color)
  color: var(--page-form-text-color)
  padding: 136px 0
  transition: background-color .3s ease, opacity .3s ease

  @media (max-width: $desktop-medium)
    padding: 48px 0

  @media (max-width: $desktop-big)
    padding: 64px 0

  @media (max-width: $mobile-size)
    padding: 32px 16px !important

  &--layout
    &-text-aside
      .page-form__content
        display: flex
        justify-content: space-between
        gap: 85px

        @media (max-width: $mobile-size)
          flex-direction: column
          gap: 0

      .page-form__header
        flex-basis: 30%

        @media (max-width: $mobile-size)
          flex-basis: unset

      .page-form__form
        flex: 1

      .form-actions
        align-self: flex-end

  &--success
    background: $cyan
    color: #ffffff

  &--error
    background: #EB7C7C
    color: #ffffff

  &__inner
    width: 100%

  &__header
    margin-bottom: 32px

  &__header-actions
    margin-top: 56px
    display: inline-flex
    align-items: center
    width: fit-content

    @media (max-width: $mobile-size)
      width: 100%
      min-width: 100%
      margin-top: 32px
      margin-bottom: 8px

  &__title
    font-size: 40px
    margin: 0 0 16px
    color: var(--page-form-text-color)

    @media (max-width: $tablet-size)
      font-size: 32px

    @media (max-width: $mobile-size)
      font-size: 24px

  &__description
    font-size: 19px
    line-height: 1.4

    @media (max-width: $mobile-size)
      font-size: 16px

  &__form
    display: flex
    flex-direction: column
    gap: 24px
</style>



// WEBPACK FOOTER //
// src/modules/shared/ui/public/forms/PageForm.vue